import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  TrashIcon,
  ArrowLeftIcon,
  PlusIcon,
  XMarkIcon,
  PencilIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import './Admin.css';

const PLANS = ['free', 'starter', 'pro', 'business'];
const PLAN_LABELS = { free: 'Free', starter: 'Starter', pro: 'Pro', business: 'Business' };

function PlanBadge({ plan }) {
  return <span className={`plan-badge plan-${plan}`}>{PLAN_LABELS[plan] || plan}</span>;
}

function CreateUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', email: '', password: '', plan: 'free', is_admin: false });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.post('/api/admin/users', {
        ...form,
        is_admin: form.is_admin === true || form.is_admin === 'true',
      });
      toast.success(`User "${res.data.user.email}" created`);
      onCreated(res.data.user);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create New User</h2>
          <button className="btn-icon" onClick={onClose}><XMarkIcon className="icon-sm" /></button>
        </div>
        <form onSubmit={handleSubmit} className="create-user-form">
          <div className="form-row">
            <label>Name</label>
            <input
              type="text"
              value={form.name}
              onChange={e => set('name', e.target.value)}
              placeholder="Full name"
              required
            />
          </div>
          <div className="form-row">
            <label>Email</label>
            <input
              type="email"
              value={form.email}
              onChange={e => set('email', e.target.value)}
              placeholder="user@example.com"
              required
            />
          </div>
          <div className="form-row">
            <label>Password</label>
            <input
              type="password"
              value={form.password}
              onChange={e => set('password', e.target.value)}
              placeholder="Min 6 characters"
              minLength={6}
              required
            />
          </div>
          <div className="form-row form-row-split">
            <div>
              <label>Plan</label>
              <select value={form.plan} onChange={e => set('plan', e.target.value)}>
                {PLANS.map(p => <option key={p} value={p}>{PLAN_LABELS[p]}</option>)}
              </select>
            </div>
            <div className="form-row-check">
              <label>
                <input
                  type="checkbox"
                  checked={form.is_admin}
                  onChange={e => set('is_admin', e.target.checked)}
                />
                Admin account
              </label>
            </div>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Creating…' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UserRow({ user, onPlanChange, onAdminToggle, onDelete, onExpand, expanded }) {
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const handleExpand = async () => {
    if (!expanded) {
      if (!detail) {
        setLoadingDetail(true);
        try {
          const res = await axios.get(`/api/admin/users/${user.id}`);
          setDetail(res.data);
        } catch {
          toast.error('Failed to load user details');
        } finally {
          setLoadingDetail(false);
        }
      }
    }
    onExpand(user.id);
  };

  return (
    <>
      <tr className={`user-row ${expanded ? 'expanded' : ''}`} onClick={handleExpand}>
        <td>
          <div className="user-name">{user.name || '—'}</div>
          <div className="user-email">{user.email}</div>
        </td>
        <td>
          <select
            className="plan-select"
            value={user.plan}
            onClick={e => e.stopPropagation()}
            onChange={e => onPlanChange(user.id, e.target.value)}
          >
            {PLANS.map(p => (
              <option key={p} value={p}>{PLAN_LABELS[p]}</option>
            ))}
          </select>
        </td>
        <td className="center">{user.chatbot_count}</td>
        <td className="center">{user.messages_this_month.toLocaleString()}</td>
        <td className="center">
          <input
            type="checkbox"
            className="admin-checkbox"
            checked={user.is_admin}
            onClick={e => e.stopPropagation()}
            onChange={e => onAdminToggle(user.id, e.target.checked)}
          />
        </td>
        <td className="center">
          <button
            className="btn-icon btn-delete"
            title="Delete user"
            onClick={e => { e.stopPropagation(); onDelete(user.id, user.email); }}
          >
            <TrashIcon className="icon-sm" />
          </button>
        </td>
        <td className="center">
          {expanded ? <ChevronUpIcon className="icon-sm" /> : <ChevronDownIcon className="icon-sm" />}
        </td>
      </tr>

      {expanded && (
        <tr className="detail-row">
          <td colSpan={7}>
            {loadingDetail ? (
              <div className="detail-loading">Loading...</div>
            ) : detail ? (
              <div className="detail-box">
                <div className="detail-stats">
                  <span>Messages this month: <strong>{detail.usage.messages.toLocaleString()}</strong></span>
                  <span>Tokens this month: <strong>{detail.usage.tokens.toLocaleString()}</strong></span>
                  <span>Member since: <strong>{new Date(detail.user.created_at).toLocaleDateString()}</strong></span>
                </div>
                {detail.chatbots.length === 0 ? (
                  <p className="no-chatbots">No chatbots yet.</p>
                ) : (
                  <table className="chatbot-table">
                    <thead>
                      <tr>
                        <th>Chatbot</th>
                        <th>Model</th>
                        <th>Docs</th>
                        <th>Status</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.chatbots.map(cb => (
                        <tr key={cb.id}>
                          <td>{cb.name}</td>
                          <td><code>{cb.model_name}</code></td>
                          <td className="center">{cb.doc_count}</td>
                          <td>
                            <span className={`status-dot ${cb.is_active ? 'active' : 'inactive'}`}>
                              {cb.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>{new Date(cb.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ) : null}
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Bot Category modal ───────────────────────────────────────────────────────

function CategoryModal({ category, onClose, onSaved }) {
  const isEdit = !!category;
  const [form, setForm] = useState({
    name: category?.name || '',
    description: category?.description || '',
    context_prompt: category?.context_prompt || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let res;
      if (isEdit) {
        res = await axios.put(`/api/admin/categories/${category.id}`, form);
      } else {
        res = await axios.post('/api/admin/categories', form);
      }
      toast.success(isEdit ? 'Category updated' : `Category "${res.data.category.name}" created`);
      onSaved(res.data.category, isEdit);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box modal-box-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Category' : 'New Bot Category'}</h2>
          <button className="btn-icon" onClick={onClose}><XMarkIcon className="icon-sm" /></button>
        </div>
        <form onSubmit={handleSubmit} className="create-user-form">
          <div className="form-row">
            <label>Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={e => set('name', e.target.value)}
              placeholder="e.g. Travel Agent, Legal, Customer Support"
              required
            />
          </div>
          <div className="form-row">
            <label>Description</label>
            <input
              type="text"
              value={form.description}
              onChange={e => set('description', e.target.value)}
              placeholder="Short description shown to users in the bot builder"
            />
          </div>
          <div className="form-row">
            <label>Context Prompt</label>
            <textarea
              value={form.context_prompt}
              onChange={e => set('context_prompt', e.target.value)}
              rows={10}
              placeholder={
                'Knowledge injected into every bot of this category.\n\n' +
                'Example for Travel Agent:\n' +
                'Schengen Zone — complete member list (27 countries):\n' +
                'Austria, Belgium, Croatia, Czech Republic, Denmark, Estonia,\n' +
                'Finland, France, Germany, Greece, Hungary, Iceland, Italy,\n' +
                'Latvia, Liechtenstein, Lithuania, Luxembourg, Malta, Netherlands,\n' +
                'Norway, Poland, Portugal, Slovakia, Slovenia, Spain, Sweden, Switzerland.\n\n' +
                'Rule: if a document gives a fee for "Schengen", that fee applies to ALL 27\n' +
                'countries above — even if only a few are named in the document.'
              }
              style={{ resize: 'vertical', fontFamily: 'monospace', fontSize: '0.85rem' }}
            />
            <small style={{ color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
              This text is injected before the knowledge-base context for every bot in this category.
              Write factual knowledge, terminology, or reasoning rules the LLM should always apply.
            </small>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Update Category' : 'Create Category'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Categories tab ───────────────────────────────────────────────────────────

function CategoriesTab() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'new' | category object

  useEffect(() => {
    axios.get('/api/admin/categories')
      .then(r => setCategories(r.data.categories))
      .catch(() => toast.error('Failed to load categories'))
      .finally(() => setLoading(false));
  }, []);

  const handleSaved = (savedCat, isEdit) => {
    if (isEdit) {
      setCategories(prev => prev.map(c => c.id === savedCat.id ? savedCat : c));
    } else {
      setCategories(prev => [...prev, savedCat].sort((a, b) => a.name.localeCompare(b.name)));
    }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"? Bots using it will lose their category context.`)) return;
    try {
      await axios.delete(`/api/admin/categories/${cat.id}`);
      setCategories(prev => prev.filter(c => c.id !== cat.id));
      toast.success('Category deleted');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete category');
    }
  };

  if (loading) return <div className="loading" style={{ padding: '2rem' }}>Loading…</div>;

  return (
    <div className="categories-tab">
      {modal && (
        <CategoryModal
          category={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}

      <div className="cat-toolbar">
        <p className="cat-description">
          Categories add specialized knowledge to bots — e.g. a <strong>Travel Agent</strong> category
          can include Schengen membership lists, reasoning rules, or domain vocabulary injected
          automatically for every bot in that category.
        </p>
        <button className="btn btn-primary" onClick={() => setModal('new')}>
          <PlusIcon className="icon-sm" /> New Category
        </button>
      </div>

      {categories.length === 0 ? (
        <div className="cat-empty">
          <TagIcon className="cat-empty-icon" />
          <p>No categories yet. Create one to add specialized context to your bots.</p>
        </div>
      ) : (
        <div className="cat-list">
          {categories.map(cat => (
            <div key={cat.id} className="cat-card">
              <div className="cat-card-header">
                <div className="cat-card-title">
                  <TagIcon className="cat-tag-icon" />
                  <strong>{cat.name}</strong>
                  {cat.description && <span className="cat-desc">{cat.description}</span>}
                </div>
                <div className="cat-card-actions">
                  <button className="btn-icon" title="Edit" onClick={() => setModal(cat)}>
                    <PencilIcon className="icon-sm" />
                  </button>
                  <button className="btn-icon btn-delete" title="Delete" onClick={() => handleDelete(cat)}>
                    <TrashIcon className="icon-sm" />
                  </button>
                </div>
              </div>
              {cat.context_prompt ? (
                <pre className="cat-context-preview">{cat.context_prompt.slice(0, 300)}{cat.context_prompt.length > 300 ? '…' : ''}</pre>
              ) : (
                <p className="cat-no-context">No context prompt set.</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Admin() {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const [reindexing, setReindexing] = useState(false);
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
    fetchSettings();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get('/api/admin/users');
      setUsers(res.data.users);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get('/api/admin/settings');
      setRegistrationEnabled(res.data.settings.registration_enabled !== 'false');
    } catch {
      // non-critical
    }
  };

  const handleRegistrationToggle = async () => {
    const newValue = !registrationEnabled;
    try {
      await axios.patch('/api/admin/settings/registration_enabled', { value: String(newValue) });
      setRegistrationEnabled(newValue);
      toast.success(newValue ? 'Registration enabled' : 'Registration disabled');
    } catch {
      toast.error('Failed to update setting');
    }
  };

  const handlePlanChange = async (userId, plan) => {
    try {
      const res = await axios.patch(`/api/admin/users/${userId}/plan`, { plan });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, plan: res.data.user.plan } : u));
      toast.success(`Plan updated to ${PLAN_LABELS[plan]}`);
    } catch {
      toast.error('Failed to update plan');
    }
  };

  const handleAdminToggle = async (userId, isAdmin) => {
    try {
      const res = await axios.patch(`/api/admin/users/${userId}/admin`, { is_admin: isAdmin });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_admin: res.data.user.is_admin } : u));
      toast.success(isAdmin ? 'Admin access granted' : 'Admin access removed');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update admin status');
    }
  };

  const handleDelete = async (userId, email) => {
    if (!window.confirm(`Delete user "${email}" and all their data? This cannot be undone.`)) return;
    try {
      await axios.delete(`/api/admin/users/${userId}`);
      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success('User deleted');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleExpand = (userId) => {
    setExpandedId(prev => prev === userId ? null : userId);
  };

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    return !q || u.email.toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q);
  });

  const handleUserCreated = (newUser) => {
    setUsers(prev => [{ ...newUser, chatbot_count: 0, messages_this_month: 0, tokens_this_month: 0 }, ...prev]);
  };

  const handleReindexAll = async () => {
    if (!window.confirm(
      'This will re-generate vector embeddings for ALL knowledge base documents using the current (multilingual) embedding model.\n\n' +
      'This is required after the model upgrade for Bengali/Banglish support to work correctly.\n\n' +
      'Continue?'
    )) return;
    setReindexing(true);
    try {
      const res = await axios.post('/api/admin/reindex-all');
      const { processed, failed, message } = res.data;
      if (failed > 0) {
        toast.error(`Re-index complete: ${processed} OK, ${failed} failed`);
      } else {
        toast.success(message || `Re-indexed ${processed} document(s) successfully`);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Re-index failed');
    } finally {
      setReindexing(false);
    }
  };

  if (loading) return <div className="loading">Loading admin panel...</div>;

  return (
    <div className="admin-page">
      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} onCreated={handleUserCreated} />}
      <header className="admin-header">
        <div className="admin-header-inner">
          <button className="btn-back" onClick={() => navigate('/dashboard')}>
            <ArrowLeftIcon className="icon-sm" /> Dashboard
          </button>
          <h1>Admin Panel</h1>
          <div className="registration-toggle">
            <span className="toggle-label">Public Registration</span>
            <button
              className={`toggle-btn ${registrationEnabled ? 'on' : 'off'}`}
              onClick={handleRegistrationToggle}
              title={registrationEnabled ? 'Click to disable registration' : 'Click to enable registration'}
            >
              <span className="toggle-knob" />
            </button>
            <span className={`toggle-status ${registrationEnabled ? 'on' : 'off'}`}>
              {registrationEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          <span className="admin-count">{users.length} user{users.length !== 1 ? 's' : ''}</span>
        </div>
      </header>

      <main className="admin-main">
        <div className="admin-tabs">
          <button
            className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            Users
          </button>
          <button
            className={`admin-tab ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => setActiveTab('categories')}
          >
            <TagIcon className="icon-sm" style={{ marginRight: '0.3rem' }} />
            Bot Categories
          </button>
        </div>

        {activeTab === 'categories' && <CategoriesTab />}

        {activeTab === 'users' && (
          <div className="admin-toolbar">
            <div className="toolbar-left">
              <div className="search-box">
                <MagnifyingGlassIcon className="search-icon" />
                <input
                  type="text"
                  placeholder="Search by name or email…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="search-input"
                />
              </div>
            </div>
            <div className="toolbar-right">
              <button
                className="btn btn-reindex"
                onClick={handleReindexAll}
                disabled={reindexing}
                title="Re-generate all knowledge base embeddings with the multilingual model"
              >
                {reindexing ? 'Re-indexing…' : '🌐 Re-index All Docs'}
              </button>
              <button className="btn btn-primary btn-new-user" onClick={() => setShowCreate(true)}>
                <PlusIcon className="icon-sm" /> New User
              </button>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          filtered.length === 0 ? (
            <p className="no-results">No users match your search.</p>
          ) : (
            <div className="table-wrap">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Plan</th>
                    <th className="center">Chatbots</th>
                    <th className="center">Msgs/month</th>
                    <th className="center">Admin</th>
                    <th className="center">Delete</th>
                    <th className="center"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(u => (
                    <UserRow
                      key={u.id}
                      user={u}
                      onPlanChange={handlePlanChange}
                      onAdminToggle={handleAdminToggle}
                      onDelete={handleDelete}
                      onExpand={handleExpand}
                      expanded={expandedId === u.id}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </main>
    </div>
  );
}

export default Admin;